import {ref} from 'vue';
import {defineStore} from 'pinia';

import {getAppContext} from '@/core/plugins/appContext';
import CURRENCY from '@/core/constants/currency';

export const useCurrencyRatesStore = defineStore('currency-rates', () => {
    const {$api} = getAppContext();

    const internetRates = ref([]);
    const officeRates = ref([]);
    const otherRates = ref([]);
    const internetArchiveRates = ref([]);
    const officeArchiveRates = ref([]);
    const otherArchiveRates = ref([]);
    const crossRate = ref([]);
    const mainPageRates = ref([]);
    const screenRates = ref([]);

    /**
     * @param {object} props
     * @param {string} props.currencyCode
     * @param {'OFFICE|INTERNET|SCREEN|MAIN_PAGE'} props.type
     * @return {boolean}
     * @private
     */
    function _isCurrencyCodeAllowed({currencyCode, type}) {
        return CURRENCY.ALLOWED_CURRENCY_CODES[type].includes(currencyCode?.toUpperCase());
    }

    async function getInternetRates() {
        try {
            const {data} = await $api.rates.internet();
            internetRates.value = data.filter(({currencyCode}) => {
                return _isCurrencyCodeAllowed({currencyCode, type: 'INTERNET'});
            });

            crossRate.value = data.find(({currencyCode}) => {
                return ['EUR/USD', 'USD/EUR'].includes(currencyCode);
            }) || null;

            return data;
        } catch (error) {
            console.error(error);
        }
    }

    async function getOfficeRates() {
        try {
            const {data} = await $api.rates.office();

            officeRates.value = data.filter(({currencyCode}) => {
                return _isCurrencyCodeAllowed({currencyCode, type: 'OFFICE'});
            });

            screenRates.value = data.filter(({currencyCode}) => {
                return _isCurrencyCodeAllowed({currencyCode, type: 'SCREEN'});
            });

            mainPageRates.value = data.filter(({currencyCode}) => {
                return _isCurrencyCodeAllowed({currencyCode, type: 'MAIN_PAGE'});
            });

            return data;
        } catch (error) {
            console.error(error);
        }
    }

    async function getOtherRates() {
        try {
            const {data} = await $api.rates.other();
            otherRates.value = data.filter(({currencyCode}) => {
                return _isCurrencyCodeAllowed({currencyCode, type: 'OTHER'});
            });

            return data;
        } catch (error) {
            console.error(error);
        }
    }

    async function getInternetArchiveRates() {
        try {
            const {data} = await $api.rates.internetArchive();
            internetArchiveRates.value = data;

            return data;
        } catch (error) {
            console.error(error);
        }
    }

    async function getOfficeArchiveRates() {
        try {
            const {data} = await $api.rates.officeArchive();
            officeArchiveRates.value = data;

            return data;
        } catch (error) {
            console.error(error);
        }
    }

    async function getOtherArchiveRates() {
        try {
            const {data} = await $api.rates.otherArchive();
            otherArchiveRates.value = data;

            return data;
        } catch (error) {
            console.error(error);
        }
    }

    return {
        internetRates,
        officeRates,
        otherRates,
        internetArchiveRates,
        officeArchiveRates,
        otherArchiveRates,
        screenRates,
        crossRate,
        mainPageRates,
        getInternetRates,
        getOfficeRates,
        getOtherRates,
        getInternetArchiveRates,
        getOfficeArchiveRates,
        getOtherArchiveRates,
    };
});
